import type { MainPropertyType, XMLPropertyResult } from '@aloft/toolkit-shared';

import { extractAdditionalFeatures } from './extractAdditionalFeatures.ts';
import { extractAge } from './extractAge.ts';
import { extractBasement } from './extractBasement.ts';
import { extractCarStorage } from './extractCarStorage.ts';
import { extractConcessions } from './extractConcessions.ts';
import { extractCondition } from './extractCondition.ts';
import { extractDataSourceDescription } from './extractDataSourceDescription.ts';
import { extractDateOfSale } from './extractDateOfSale.ts';
import { extractGLA } from './extractGLA.ts';
import { extractLocation } from './extractLocation.ts';
import { extractLocationTag } from './extractLocationTag.ts';
import { extractNumberOfUnits } from './extractNumberOfUnits.ts';
import { extractPorchPatioDeck } from './extractPorchPatioDeck.ts';
import { extractPrice } from './extractPrice.ts';
import { extractPropertyType } from './extractPropertyType.ts';
import { extractQuality } from './extractQuality.ts';
import { extractRoomCounts } from './extractRoomCounts.ts';
import { extractSiteArea } from './extractSiteArea.ts';
import { extractTermsOfSale } from './extractTermsOfSale.ts';
import { extractView } from './extractView.ts';
import type { AdditionalFeatureLabels, AdditionalFeatures } from './types.ts';

export const extractPropertyResult = (
  sale: Element,
  additionalFeatures:
    | { t: 'subject'; features: AdditionalFeatures }
    | { t: 'comparable'; labels: AdditionalFeatureLabels },
  defaultPropertyType: MainPropertyType,
): XMLPropertyResult => {
  const propertyType = extractPropertyType(sale, defaultPropertyType);
  const { address, latitude, longitude } = extractLocationTag(sale);
  const { mls, mlsListingID, daysOnMarket } = extractDataSourceDescription(sale);
  const price = extractPrice(sale);
  const age = extractAge(sale);
  const { additionalFeature1, additionalFeature2, additionalFeature3 } =
    additionalFeatures.t === 'subject'
      ? {
          additionalFeature1: additionalFeatures.features.feature1,
          additionalFeature2: additionalFeatures.features.feature2,
          additionalFeature3: additionalFeatures.features.feature3,
        }
      : extractAdditionalFeatures(sale, {
          featureLabel1: additionalFeatures.labels.featureLabel1,
          featureLabel2: additionalFeatures.labels.featureLabel2,
          featureLabel3: additionalFeatures.labels.featureLabel3,
        });

  const numberOfUnits = propertyType === 'multiFamily' ? extractNumberOfUnits(sale) : undefined;
  const gla = extractGLA(sale);
  const { fullBaths, halfBaths } = extractRoomCounts(sale);
  const basement = extractBasement(sale);
  const { garageSpaces, carportSpaces } = extractCarStorage(sale);
  const { status, contractDate, saleDate } = extractDateOfSale(sale);
  const siteArea = extractSiteArea(sale);
  const location = extractLocation(sale);
  const view = extractView(sale);
  const concessions = extractConcessions(sale);
  const termsOfSale = extractTermsOfSale(sale);
  const quality = extractQuality(sale);
  const condition = extractCondition(sale);
  const porchPatioDeck = extractPorchPatioDeck(sale);

  return {
    address,
    age,
    gla,
    fullBaths,
    halfBaths,
    basement,
    garageSpaces,
    carportSpaces,
    numberOfUnits,
    status,
    price,
    saleDate,
    contractDate,
    daysOnMarket,
    latitude,
    longitude,
    mls,
    mlsListingID,
    siteArea,
    location,
    view,
    quality,
    condition,
    concessions,
    termsOfSale,
    propertyType,
    porchPatioDeck,
    additionalFeature1,
    additionalFeature2,
    additionalFeature3,
  };
};
